import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Users, RefreshCw, Clock, LogIn, LogOut, UserPlus, Trash2, ScanFace } from 'lucide-react';

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any }> = ({ children, className = '', title, icon: Icon }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
    {(title || Icon) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3 bg-white/[0.02]">
        {Icon && <Icon className="w-5 h-5 text-blue-400" />}
        {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase">{title}</h3>}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

interface AttendanceRecord {
  id: string;
  plate: string;
  camera: string;
  checkIn: Date;
  checkOut: Date | null;
  minutesOnSite: number | null;
}

interface StaffMember {
  _id: string;
  name: string;
  employee_id: string;
  department: string;
  status: string;
  photos: any[];
}

interface FaceAttendanceEvent {
  id: string;
  employee_id: string;
  staffName: string;
  timestamp: Date;
  confidence: number;
}

function authHeaders() {
  const token = localStorage.getItem('csense_auth_token');
  return { 'Authorization': `Bearer ${token}` };
}

export function AIAttendance() {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [faceEvents, setFaceEvents] = useState<FaceAttendanceEvent[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffDept, setNewStaffDept] = useState('');
  const [enrollingFor, setEnrollingFor] = useState<string | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/alerts?usecase=worker-time-tracking&limit=200', {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const events = (data.alerts || []).map((a: any) => ({
          plate: a.metadata?.plate_number || 'UNKNOWN',
          camera: a.cameraName || 'Unknown Camera',
          type: a.type,
          timestamp: a.timestamp ? new Date(a.timestamp) : new Date(),
          minutesOnSite: a.metadata?.time_on_premises ? Math.round(parseFloat(a.metadata.time_on_premises) / 60) : null
        })).sort((a: any, b: any) => a.timestamp.getTime() - b.timestamp.getTime());

        // Pair entries with their next exit for the same plate
        const open: Record<string, any> = {};
        const paired: AttendanceRecord[] = [];
        events.forEach((e: any, idx: number) => {
          if (e.type === 'worker_entry') {
            open[e.plate] = { id: `${e.plate}-${idx}`, plate: e.plate, camera: e.camera, checkIn: e.timestamp, checkOut: null, minutesOnSite: null };
          } else if (e.type === 'worker_exit' && open[e.plate]) {
            open[e.plate].checkOut = e.timestamp;
            open[e.plate].minutesOnSite = e.minutesOnSite;
            paired.push(open[e.plate]);
            delete open[e.plate];
          }
        });
        // Anyone still "open" hasn't checked out yet
        Object.values(open).forEach((rec: any) => paired.push(rec));

        setRecords(paired.sort((a, b) => b.checkIn.getTime() - a.checkIn.getTime()));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStaffAndFaceEvents = useCallback(async () => {
    setStaffLoading(true);
    try {
      const [staffRes, attendanceRes] = await Promise.all([
        fetch('/api/staff', { headers: authHeaders() }),
        fetch('/api/attendance', { headers: authHeaders() })
      ]);
      let staffList: StaffMember[] = [];
      if (staffRes.ok) {
        const data = await staffRes.json();
        staffList = data.staff || [];
        setStaff(staffList);
      }
      if (attendanceRes.ok) {
        const data = await attendanceRes.json();
        const nameByEmployeeId: Record<string, string> = {};
        staffList.forEach(s => { nameByEmployeeId[s.employee_id] = s.name; });
        const events = (data.attendance || []).map((a: any, idx: number) => ({
          id: a.alert_id || `${a.metadata?.employee_id}-${idx}`,
          employee_id: a.metadata?.employee_id || 'unknown',
          staffName: nameByEmployeeId[a.metadata?.employee_id] || a.metadata?.employee_id || 'Unknown',
          timestamp: a.timestamp ? new Date(a.timestamp) : new Date(),
          confidence: a.confidence || 0
        })).sort((a: any, b: any) => b.timestamp.getTime() - a.timestamp.getTime());
        setFaceEvents(events);
      }
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); fetchStaffAndFaceEvents(); }, [fetchRecords, fetchStaffAndFaceEvents]);

  const addStaff = async () => {
    if (!newStaffName.trim()) return;
    setStaffError(null);
    try {
      const res = await fetch('/api/staff', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newStaffName.trim(), department: newStaffDept.trim() })
      });
      if (res.ok) {
        setNewStaffName('');
        setNewStaffDept('');
        fetchStaffAndFaceEvents();
      } else {
        const err = await res.json().catch(() => ({}));
        setStaffError(err.detail || 'Failed to add staff member');
      }
    } catch (e) {
      setStaffError('Failed to add staff member');
    }
  };

  const deleteStaff = async (employeeId: string) => {
    await fetch(`/api/staff/${employeeId}`, { method: 'DELETE', headers: authHeaders() });
    fetchStaffAndFaceEvents();
  };

  const triggerEnroll = (employeeId: string) => {
    setEnrollingFor(employeeId);
    fileInputRef.current?.click();
  };

  const onPhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const employeeId = enrollingFor;
    e.target.value = '';
    if (!file || !employeeId) return;

    setStaffError(null);
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      try {
        const res = await fetch(`/api/staff/${employeeId}/photos`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_base64: base64 })
        });
        if (res.ok) {
          fetchStaffAndFaceEvents();
        } else {
          const err = await res.json().catch(() => ({}));
          setStaffError(err.detail || 'Failed to enroll photo');
        }
      } catch (err) {
        setStaffError('Failed to enroll photo');
      }
    };
    reader.readAsDataURL(file);
  };

  const onSite = records.filter(r => !r.checkOut).length;
  const totalToday = records.filter(r => r.checkIn.toDateString() === new Date().toDateString()).length;

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoSelected} />

      <div>
        <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-3"><Users className="w-8 h-8 text-blue-400" /> AI Attendance</h1>
        <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Entry/exit tracking derived from plate-based worker time tracking cameras.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Currently On Site</p><h4 className="text-2xl font-black text-emerald-400">{onSite}</h4></GlassPanel>
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Check-ins Today</p><h4 className="text-2xl font-black text-white">{totalToday}</h4></GlassPanel>
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Total Tracked</p><h4 className="text-2xl font-black text-white">{records.length}</h4></GlassPanel>
      </div>

      <GlassPanel className="p-0" title="Vehicle / Worker Time Tracking Log" icon={Clock}>
        <div className="flex justify-end mb-4">
          <button onClick={fetchRecords} className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-100/60 hover:text-white flex items-center gap-2 text-xs uppercase font-bold">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : records.length === 0 ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">No worker time tracking events yet. Assign the "Worker Time Tracking" usecase to a camera to start.</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Plate / ID</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Camera</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Check In</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Check Out</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Time On Site</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-3 text-sm font-mono font-bold text-white">{r.plate}</td>
                  <td className="py-3 text-xs text-blue-100/60">{r.camera}</td>
                  <td className="py-3 text-xs text-blue-100/60 flex items-center gap-1"><LogIn className="w-3 h-3 text-emerald-400" />{r.checkIn.toLocaleString()}</td>
                  <td className="py-3 text-xs text-blue-100/60">
                    {r.checkOut ? <span className="flex items-center gap-1"><LogOut className="w-3 h-3 text-red-400" />{r.checkOut.toLocaleString()}</span> : <span className="text-emerald-400 font-bold uppercase text-[10px]">On Site</span>}
                  </td>
                  <td className="py-3 text-xs text-blue-100/60">{r.minutesOnSite !== null ? `${r.minutesOnSite} min` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassPanel>

      <div>
        <h2 className="text-xl font-black tracking-tighter uppercase flex items-center gap-3"><ScanFace className="w-6 h-6 text-teal-400" /> Staff Face Recognition</h2>
        <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Enroll staff photos here, then assign "Staff Face Recognition & Attendance" to a camera in the Cameras tab.</p>
      </div>

      <GlassPanel title="Enroll Staff" icon={UserPlus}>
        {staffError && <p className="text-red-400 text-sm mb-4">{staffError}</p>}
        <div className="flex flex-wrap gap-3 mb-6">
          <input value={newStaffName} onChange={e => setNewStaffName(e.target.value)} placeholder="Staff name" className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500/50" />
          <input value={newStaffDept} onChange={e => setNewStaffDept(e.target.value)} placeholder="Department (optional)" className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500/50" />
          <button onClick={addStaff} className="px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl font-bold text-sm">Add Staff</button>
        </div>

        {staffLoading ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : staff.length === 0 ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">No staff enrolled yet.</div>
        ) : (
          <div className="space-y-2">
            {staff.map(s => (
              <div key={s.employee_id} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl">
                <div>
                  <p className="font-bold text-white">{s.name}</p>
                  <p className="text-xs text-blue-100/40">{s.employee_id}{s.department ? ` • ${s.department}` : ''} • {s.photos?.length || 0} photo(s) enrolled</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => triggerEnroll(s.employee_id)} className="px-4 py-2 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-xl text-xs font-bold uppercase hover:bg-teal-500/20">
                    + Photo
                  </button>
                  <button onClick={() => deleteStaff(s.employee_id)} className="p-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl hover:bg-red-500/20">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      <GlassPanel className="p-0" title="Face Recognition Attendance Log" icon={ScanFace}>
        <div className="flex justify-end mb-4">
          <button onClick={fetchStaffAndFaceEvents} className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-100/60 hover:text-white flex items-center gap-2 text-xs uppercase font-bold">
            <RefreshCw className={`w-3.5 h-3.5 ${staffLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {faceEvents.length === 0 ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">No face recognition check-ins yet. Enroll a staff photo above, then assign the usecase to a camera.</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Staff</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Employee ID</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Check-In Time</th>
                <th className="py-3 text-[10px] uppercase tracking-widest text-blue-100/40">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {faceEvents.map(ev => (
                <tr key={ev.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-3 text-sm font-bold text-white">{ev.staffName}</td>
                  <td className="py-3 text-xs text-blue-100/60 font-mono">{ev.employee_id}</td>
                  <td className="py-3 text-xs text-blue-100/60 flex items-center gap-1"><LogIn className="w-3 h-3 text-emerald-400" />{ev.timestamp.toLocaleString()}</td>
                  <td className="py-3 text-xs text-blue-100/60">{(ev.confidence * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassPanel>
    </div>
  );
}
